import { useMemo, useState, useContext } from 'react';
import {
  MagnifyingGlassIcon, UserCircleIcon, ChevronDownIcon,
  UsersIcon, BoltIcon,
} from '@heroicons/react/24/outline';
import { useTrackedCases } from '../../../hooks/useTrackedCases';
import { AuthContext } from '../../../contexts/Auth/Auth.Context';
import { maskName } from '../../../utils/privacy';
import { splitSpecies } from '../../../utils/species';
import MaskedName from '../../../components/MaskedName';

interface Props { allData: any[] }

/* ── Constants ──────────────────────────────────────────────────────────── */

const STATUS_CLS: Record<string, string> = {
  ongoing:   'bg-violet-500/15 text-violet-400 border-violet-500/20',
  concluded: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20',
  absconded: 'bg-amber-500/15 text-amber-400 border-amber-500/20',
};

const RISK_STYLE = {
  HIGH:   { badge: 'bg-red-500/15 text-red-400 border-red-500/25',       dot: 'bg-red-500',    bar: 'bg-red-500/60' },
  MEDIUM: { badge: 'bg-amber-500/15 text-amber-400 border-amber-500/25', dot: 'bg-amber-500',  bar: 'bg-amber-500/60' },
  LOW:    { badge: 'bg-white/8 text-white/35 border-white/12',            dot: 'bg-white/25',   bar: 'bg-green-700/50' },
};

const HIGH_VALUE_KW = ['ivory', 'elephant', 'rhino', 'rhinoceros', 'pangolin', 'leopard', 'lion', 'cheetah', 'tiger', 'tortoise', 'abalone'];

const SPECIES_CHIP: Record<string, string> = {
  ivory:       'bg-yellow-500/15 text-yellow-300 border-yellow-500/20',
  elephant:    'bg-yellow-500/15 text-yellow-300 border-yellow-500/20',
  rhino:       'bg-red-500/15 text-red-400 border-red-500/20',
  rhinoceros:  'bg-red-500/15 text-red-400 border-red-500/20',
  pangolin:    'bg-orange-500/15 text-orange-400 border-orange-500/20',
  leopard:     'bg-amber-500/15 text-amber-400 border-amber-500/20',
  lion:        'bg-amber-500/15 text-amber-400 border-amber-500/20',
  abalone:     'bg-cyan-500/15 text-cyan-400 border-cyan-500/20',
  tortoise:    'bg-green-500/15 text-green-400 border-green-500/20',
};

/* ── Helpers ────────────────────────────────────────────────────────────── */

function computeRisk(cases: any[]): 'HIGH' | 'MEDIUM' | 'LOW' {
  const hasHighValue = cases.some((c: any) =>
    HIGH_VALUE_KW.some((kw) => c.species?.toLowerCase().includes(kw))
  );
  const absconded = cases.some((c: any) => c.caseStatus?.toLowerCase() === 'absconded');
  const n = cases.length;
  if (absconded || n >= 5 || (n >= 3 && hasHighValue)) return 'HIGH';
  if (n >= 2 || hasHighValue) return 'MEDIUM';
  return 'LOW';
}

function speciesChipCls(sp: string): string {
  const lower = sp.toLowerCase();
  for (const [kw, cls] of Object.entries(SPECIES_CHIP)) {
    if (lower.includes(kw)) return cls;
  }
  return 'bg-white/8 text-white/40 border-white/12';
}

function extractYear(dateStr: string | undefined): number | null {
  if (!dateStr) return null;
  const m = dateStr.match(/\b(19|20)\d{2}\b/);
  return m ? parseInt(m[0]) : null;
}

/* ── Activity sparkline ─────────────────────────────────────────────────── */

function ActivitySparkline({ cases }: { cases: any[] }) {
  const bars = useMemo(() => {
    const map: Record<number, number> = {};
    const now = new Date().getFullYear();
    cases.forEach((c: any) => {
      const y = extractYear(c.dateOfSeizure || c.dateOfArrest);
      if (y && y >= 2005 && y <= now) map[y] = (map[y] || 0) + 1;
    });
    const sorted = Object.keys(map).map(Number).sort();
    if (!sorted.length) return [];
    const result: { year: number; count: number }[] = [];
    for (let y = sorted[0]; y <= sorted[sorted.length - 1]; y++) {
      result.push({ year: y, count: map[y] || 0 });
    }
    return result;
  }, [cases]);

  if (!bars.length) {
    return <p className="text-white/20 text-[10px] italic">No date data available</p>;
  }

  const peak = Math.max(...bars.map((b) => b.count), 1);

  return (
    <div>
      <p className="text-white/30 text-[9px] uppercase tracking-wider mb-2">Activity Timeline</p>
      <div className="flex items-end gap-0.5" style={{ height: 38 }}>
        {bars.map(({ year, count }) => (
          <div key={year} className="flex flex-col items-center gap-0.5 flex-1 min-w-0" title={`${year}: ${count} case${count !== 1 ? 's' : ''}`}>
            <div
              className="w-full rounded-sm bg-amber-500/60 transition-all"
              style={{ height: `${Math.max((count / peak) * 32, 2)}px` }}
            />
            {bars.length <= 9 && (
              <span className="text-white/20 text-[7px] tabular-nums leading-none">{String(year).slice(2)}</span>
            )}
          </div>
        ))}
      </div>
      {bars.length > 9 && (
        <div className="flex justify-between mt-0.5">
          <span className="text-white/20 text-[8px]">{bars[0].year}</span>
          <span className="text-white/20 text-[8px]">{bars[bars.length - 1].year}</span>
        </div>
      )}
    </div>
  );
}

/* ── Main component ─────────────────────────────────────────────────────── */

export default function OffenderIntelligenceView({ allData }: Props) {
  const { state } = useContext(AuthContext);
  const role = state.userData?.role?.toLowerCase() ?? 'guest';

  const [search,           setSearch]           = useState('');
  const [sortBy,           setSortBy]           = useState<'risk' | 'count' | 'name'>('risk');
  const [expanded,         setExpanded]         = useState<string | null>(null);
  const [selectedNat,      setSelectedNat]      = useState<string | null>(null);
  const [selectedSyndicate,setSelectedSyndicate]= useState<string[] | null>(null);
  const [riskFilter,       setRiskFilter]       = useState<'ALL' | 'HIGH' | 'MEDIUM' | 'LOW'>('ALL');

  const { trackedIds, toggle, isLoggedIn } = useTrackedCases();

  /* ── Build offender profiles ── */
  const offenders = useMemo(() => {
    const map: Record<string, { name: string; nationality: string; cases: any[] }> = {};
    allData.forEach((d: any) => {
      const name = d.nameOfAccused?.trim();
      if (!name) return;
      const key = name.toLowerCase();
      if (!map[key]) map[key] = { name, nationality: d.nationality || '—', cases: [] };
      map[key].cases.push(d);
    });
    return Object.values(map).map((o) => ({
      ...o,
      risk:    computeRisk(o.cases),
      species: Array.from(new Set(
        o.cases.flatMap((c: any) => splitSpecies(c.species))
      )) as string[],
    }));
  }, [allData]);

  /* ── Nationality stats ── */
  const natStats = useMemo(() => {
    const map: Record<string, number> = {};
    allData.forEach((d: any) => {
      const n = d.nationality?.toUpperCase();
      if (n) map[n] = (map[n] || 0) + 1;
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 10);
  }, [allData]);

  /* ── Syndicate detection via BFS on shared-case adjacency ── */
  const syndicates = useMemo(() => {
    // Build: caseKey → [offenderName, ...]
    const caseMap: Record<string, string[]> = {};
    allData.forEach((d: any) => {
      const name = d.nameOfAccused?.trim();
      if (!name) return;
      // Use a composite key to identify unique cases
      const key = [d.caseNumber, d.location, d.dateOfSeizure].filter(Boolean).join('|') || `row_${Math.random()}`;
      if (!caseMap[key]) caseMap[key] = [];
      if (!caseMap[key].includes(name)) caseMap[key].push(name);
    });

    // Build adjacency
    const adj: Record<string, Set<string>> = {};
    Object.values(caseMap).forEach((names) => {
      if (names.length < 2) return;
      names.forEach((a) => {
        names.forEach((b) => {
          if (a === b) return;
          if (!adj[a]) adj[a] = new Set();
          adj[a].add(b);
        });
      });
    });

    // BFS connected components
    const visited = new Set<string>();
    const clusters: { members: string[]; sharedCases: number }[] = [];
    Object.keys(adj).forEach((start) => {
      if (visited.has(start)) return;
      const component: string[] = [];
      const queue = [start];
      while (queue.length) {
        const node = queue.shift()!;
        if (visited.has(node)) continue;
        visited.add(node);
        component.push(node);
        adj[node]?.forEach((n) => { if (!visited.has(n)) queue.push(n); });
      }
      if (component.length >= 2) {
        const sharedCases = Object.values(caseMap).filter(
          (names) => component.some((m) => names.includes(m)) && names.length >= 2
        ).length;
        clusters.push({ members: component, sharedCases });
      }
    });

    return clusters.sort((a, b) => b.members.length - a.members.length);
  }, [allData]);

  /* ── Intelligence alerts ── */
  const alerts = useMemo(() => {
    const items: { level: 'critical' | 'warning' | 'info'; text: string }[] = [];

    const abscondedCount = offenders.filter((o) =>
      o.cases.some((c: any) => c.caseStatus?.toLowerCase() === 'absconded')
    ).length;
    const highRiskCount = offenders.filter((o) => o.risk === 'HIGH').length;
    const repeatCount   = offenders.filter((o) => o.cases.length > 1).length;
    const bigSyndicate  = syndicates[0];

    if (abscondedCount > 0)
      items.push({ level: 'critical', text: `${abscondedCount} offender${abscondedCount > 1 ? 's' : ''} currently absconded — active fugitive alert, immediate action required` });

    if (bigSyndicate?.members.length >= 3)
      items.push({ level: 'critical', text: `Suspected wildlife trafficking syndicate of ${bigSyndicate.members.length} individuals mapped across ${bigSyndicate.sharedCases} shared case${bigSyndicate.sharedCases !== 1 ? 's' : ''}` });

    if (highRiskCount > 0)
      items.push({ level: 'warning', text: `${highRiskCount} HIGH-risk offender${highRiskCount > 1 ? 's' : ''} identified — priority surveillance and cross-border notification recommended` });

    if (repeatCount > 0)
      items.push({ level: 'warning', text: `${repeatCount} repeat offender${repeatCount > 1 ? 's' : ''} on record — escalation to regional enforcement units advised` });

    if (syndicates.length > 1)
      items.push({ level: 'info', text: `${syndicates.length} distinct criminal networks detected — cross-case pattern analysis available` });

    return items;
  }, [offenders, syndicates]);

  /* ── Filtered + sorted list ── */
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    let list = offenders.filter((o) => {
      if (selectedNat && o.nationality.toUpperCase() !== selectedNat) return false;
      if (riskFilter !== 'ALL' && o.risk !== riskFilter) return false;
      if (selectedSyndicate && !selectedSyndicate.some((m) => m.toLowerCase() === o.name.toLowerCase())) return false;
      return !q || o.name.toLowerCase().includes(q) || o.nationality.toLowerCase().includes(q);
    });
    const RISK_ORDER = { HIGH: 0, MEDIUM: 1, LOW: 2 };
    if (sortBy === 'risk')  list = list.sort((a, b) => RISK_ORDER[a.risk] - RISK_ORDER[b.risk]);
    if (sortBy === 'count') list = list.sort((a, b) => b.cases.length - a.cases.length);
    if (sortBy === 'name')  list = list.sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [offenders, search, sortBy, selectedNat, riskFilter, selectedSyndicate]);

  const highRiskCount = offenders.filter((o) => o.risk === 'HIGH').length;
  const repeatCount   = offenders.filter((o) => o.cases.length > 1).length;
  const maxCases      = Math.max(...offenders.map((o) => o.cases.length), 1);

  return (
    <div className="flex-1 overflow-y-auto p-5" style={{ scrollbarWidth: 'thin', scrollbarColor: '#14532d transparent' }}>

      {/* Page header */}
      <div className="mb-5">
        <h2 className="text-white/85 text-base font-semibold" style={{ fontFamily: 'var(--font-display)' }}>Offender Intelligence</h2>
        <p className="text-white/30 text-xs mt-0.5">Profile, risk-score and network-analyse accused individuals across all cases</p>
      </div>

      {/* ── Intelligence Alerts ── */}
      {alerts.length > 0 && (
        <div className="mb-5 rounded-xl border border-red-500/20 overflow-hidden" style={{ background: 'rgba(185,28,28,0.05)' }}>
          <div className="px-4 py-2.5 border-b border-red-500/10 flex items-center gap-2">
            <BoltIcon className="w-3.5 h-3.5 text-red-400 shrink-0" />
            <span className="text-red-400 text-[10px] font-bold uppercase tracking-wider">Intelligence Alerts</span>
            <span className="ml-auto px-1.5 py-0.5 rounded-full bg-red-500/15 text-red-400 text-[9px] font-bold border border-red-500/20">{alerts.length}</span>
          </div>
          <div className="flex flex-col divide-y divide-white/4">
            {alerts.map((a, i) => (
              <div key={i} className="flex items-start gap-3 px-4 py-2.5">
                <span className={`mt-1 shrink-0 w-1.5 h-1.5 rounded-full ${
                  a.level === 'critical' ? 'bg-red-500' : a.level === 'warning' ? 'bg-amber-400' : 'bg-blue-400'
                }`} />
                <p className="text-white/60 text-xs leading-relaxed">{a.text}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Summary stats ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {[
          { label: 'Total Accused',    value: offenders.length,   color: 'text-white/85' },
          { label: 'HIGH Risk',        value: highRiskCount,      color: 'text-red-400' },
          { label: 'Repeat Offenders', value: repeatCount,        color: 'text-amber-400' },
          { label: 'Syndicates Found', value: syndicates.length,  color: 'text-violet-400' },
        ].map((s) => (
          <div key={s.label} className="rounded-xl border border-white/6 px-4 py-3" style={{ background: '#0a2e18' }}>
            <span className="text-white/30 text-[10px] uppercase tracking-wider block">{s.label}</span>
            <p className={`text-2xl font-bold tabular-nums mt-1 ${s.color}`} style={{ fontFamily: 'var(--font-display)' }}>{s.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

        {/* ── Left column: Nationality + Syndicates ── */}
        <div className="flex flex-col gap-4">

          {/* Nationality panel */}
          <div className="rounded-xl border border-white/6 p-5" style={{ background: '#0a2e18' }}>
            <div className="flex items-center justify-between mb-1">
              <p className="text-white/55 text-xs font-semibold tracking-[0.1em] uppercase">Offender Nationalities</p>
              {selectedNat && (
                <button
                  onClick={() => setSelectedNat(null)}
                  className="text-[9px] text-amber-400/80 hover:text-amber-300 border border-amber-500/25 rounded px-1.5 py-0.5 transition-colors"
                >
                  Clear ✕
                </button>
              )}
            </div>
            <p className="text-white/20 text-[9px] mb-3">Click to filter list</p>
            <div className="flex flex-col gap-2.5">
              {natStats.map(([nat, count]) => {
                const isActive = selectedNat === nat;
                return (
                  <div
                    key={nat}
                    role="button"
                    tabIndex={0}
                    className={`rounded-lg px-2 py-1.5 -mx-2 cursor-pointer transition-all ${
                      isActive ? 'bg-amber-500/10 ring-1 ring-amber-500/30' : 'hover:bg-white/4'
                    }`}
                    onClick={() => setSelectedNat(isActive ? null : nat)}
                    onKeyDown={(e) => e.key === 'Enter' && setSelectedNat(isActive ? null : nat)}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-xs font-medium transition-colors ${isActive ? 'text-amber-300' : 'text-white/65'}`}>{nat}</span>
                      <span className={`text-xs tabular-nums ${isActive ? 'text-amber-400' : 'text-white/35'}`}>{count}</span>
                    </div>
                    <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                      <div
                        className={`h-full rounded-full transition-all duration-700 ${isActive ? 'bg-amber-400' : 'bg-amber-500/60'}`}
                        style={{ width: `${(count / (natStats[0]?.[1] || 1)) * 100}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Syndicate / Network Clusters panel */}
          {syndicates.length > 0 && (
            <div className="rounded-xl border border-violet-500/20 p-5" style={{ background: '#0a2e18' }}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <UsersIcon className="w-3.5 h-3.5 text-violet-400 shrink-0" />
                  <p className="text-white/55 text-xs font-semibold tracking-[0.1em] uppercase">Suspected Syndicates</p>
                </div>
                {selectedSyndicate && (
                  <button
                    onClick={() => setSelectedSyndicate(null)}
                    className="text-[9px] text-violet-400/80 hover:text-violet-300 border border-violet-500/25 rounded px-1.5 py-0.5 transition-colors"
                  >
                    Clear ✕
                  </button>
                )}
              </div>
              <p className="text-white/20 text-[9px] mb-3">Offenders linked by shared cases — click to isolate</p>
              <div className="flex flex-col gap-2">
                {syndicates.slice(0, 7).map((s, i) => {
                  const membersKey = s.members.slice().sort().join('|');
                  const activeKey  = selectedSyndicate ? selectedSyndicate.slice().sort().join('|') : '';
                  const isActive   = membersKey === activeKey;
                  return (
                    <div
                      key={i}
                      role="button"
                      tabIndex={0}
                      className={`rounded-lg px-3 py-2.5 cursor-pointer transition-all border ${
                        isActive
                          ? 'border-violet-500/35 bg-violet-500/8'
                          : 'border-white/5 hover:border-violet-500/20 hover:bg-violet-500/5'
                      }`}
                      onClick={() => setSelectedSyndicate(isActive ? null : s.members)}
                      onKeyDown={(e) => e.key === 'Enter' && setSelectedSyndicate(isActive ? null : s.members)}
                    >
                      <div className="flex items-center justify-between mb-1.5">
                        <span className={`text-[10px] font-semibold ${isActive ? 'text-violet-300' : 'text-white/45'}`}>
                          Network {String(i + 1).padStart(2, '0')}
                        </span>
                        <div className="flex items-center gap-1.5">
                          <span className="px-1.5 py-0.5 rounded-full bg-violet-500/12 text-violet-400 text-[8px] border border-violet-500/20">
                            {s.members.length} members
                          </span>
                          <span className="text-white/20 text-[9px]">{s.sharedCases} case{s.sharedCases !== 1 ? 's' : ''}</span>
                        </div>
                      </div>
                      <p className="text-white/28 text-[9px] leading-relaxed truncate">
                        {s.members.slice(0, 3).join(', ')}{s.members.length > 3 ? ` +${s.members.length - 3} more` : ''}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* ── Right 2/3: Offender list ── */}
        <div className="lg:col-span-2 rounded-xl border border-white/6 overflow-hidden flex flex-col" style={{ background: '#0a2e18' }}>

          {/* Toolbar */}
          <div className="px-5 py-4 border-b border-white/5 flex items-center gap-3 flex-wrap shrink-0">
            {/* Search */}
            <div className="relative flex-1 min-w-[160px]">
              <MagnifyingGlassIcon className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-white/25" />
              <input
                className="bg-white/5 border border-white/10 text-white text-xs rounded-lg pl-8 pr-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500/40 placeholder-white/20 w-full"
                placeholder="Search name or nationality…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {/* Risk filter pills */}
            <div className="flex items-center gap-1">
              {(['ALL', 'HIGH', 'MEDIUM', 'LOW'] as const).map((r) => (
                <button
                  key={r}
                  onClick={() => setRiskFilter(r)}
                  className={`px-2 py-1 rounded-md text-[9px] font-bold uppercase transition-all border ${
                    riskFilter === r
                      ? r === 'HIGH'   ? 'bg-red-500/20 text-red-400 border-red-500/30'
                      : r === 'MEDIUM' ? 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                      : r === 'LOW'    ? 'bg-white/10 text-white/50 border-white/15'
                      :                  'bg-white/8 text-white/50 border-white/12'
                      : 'text-white/25 hover:text-white/50 border-transparent'
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>

            {/* Sort */}
            <select
              className="bg-[#0a2e18] border border-white/10 text-white/60 text-xs rounded-lg px-3 py-2 focus:outline-none appearance-none"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'risk' | 'count' | 'name')}
            >
              <option value="risk">Sort: Risk</option>
              <option value="count">Sort: Cases</option>
              <option value="name">Sort: Name</option>
            </select>

            <span className="text-white/20 text-[10px] shrink-0 tabular-nums">{filtered.length} / {offenders.length}</span>
          </div>

          {/* List body */}
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3 flex-1">
              <UserCircleIcon className="w-10 h-10 text-white/10" />
              <p className="text-white/25 text-sm">
                {selectedNat       ? `No offenders from ${selectedNat}`
                : selectedSyndicate ? 'No results for this network'
                : riskFilter !== 'ALL' ? `No ${riskFilter}-risk offenders`
                : 'No offenders found'}
              </p>
              {(selectedNat || selectedSyndicate || riskFilter !== 'ALL') && (
                <button
                  onClick={() => { setSelectedNat(null); setSelectedSyndicate(null); setRiskFilter('ALL'); }}
                  className="text-[10px] text-amber-400/70 hover:text-amber-300 transition-colors"
                >
                  Clear all filters
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-y-auto flex-1" style={{ maxHeight: 640, scrollbarWidth: 'thin', scrollbarColor: '#14532d transparent' }}>
              {filtered.map((o, idx) => {
                const isOpen  = expanded === o.name;
                const rCls    = RISK_STYLE[o.risk];
                return (
                  <div key={idx} className="border-t border-white/4 first:border-0">

                    {/* ── Row ── */}
                    <div
                      role="button"
                      tabIndex={0}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-white/[0.03] cursor-pointer transition-colors"
                      onClick={() => setExpanded(isOpen ? null : o.name)}
                      onKeyDown={(e) => e.key === 'Enter' && setExpanded(isOpen ? null : o.name)}
                    >
                      {/* Avatar + risk dot */}
                      <div className="relative shrink-0">
                        <div className="w-7 h-7 rounded-full bg-red-900/40 flex items-center justify-center text-[10px] text-red-300 font-bold">
                          {o.name[0].toUpperCase()}
                        </div>
                        <span className={`absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-[#0a2e18] ${rCls.dot}`} />
                      </div>

                      {/* Name + badges */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <MaskedName name={o.name} role={role} className="text-white/80 text-xs font-semibold" />
                          <span className={`px-1.5 py-0.5 rounded-full border text-[8px] font-bold ${rCls.badge}`}>{o.risk}</span>
                          {o.cases.length > 1 && (
                            <span className="px-1.5 py-0.5 rounded-full bg-red-500/15 text-red-400 border border-red-500/20 text-[8px] font-bold">REPEAT</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-white/30 text-[10px]">{o.nationality}</span>
                          <span className="text-white/15 text-[10px]">&bull;</span>
                          <span className="text-white/30 text-[10px]">{o.cases.length} case{o.cases.length !== 1 ? 's' : ''}</span>
                        </div>
                      </div>

                      {/* Relative bar */}
                      <div className="w-16 shrink-0 hidden sm:block">
                        <div className="h-1 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                          <div className={`h-full rounded-full ${rCls.bar}`} style={{ width: `${(o.cases.length / maxCases) * 100}%` }} />
                        </div>
                      </div>

                      {/* Top species chip (inline) */}
                      {o.species[0] && (
                        <span className={`hidden lg:inline-flex px-1.5 py-0.5 rounded-full border text-[8px] font-medium capitalize shrink-0 ${speciesChipCls(o.species[0])}`}>
                          {o.species[0]}
                        </span>
                      )}

                      <ChevronDownIcon className={`w-3.5 h-3.5 text-white/25 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                    </div>

                    {/* ── Expanded panel ── */}
                    {isOpen && (
                      <div className="border-t border-white/5 px-4 pt-4 pb-4" style={{ background: 'rgba(0,0,0,0.15)' }}>

                        {/* Top row: timeline + species profile */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">

                          {/* Activity timeline */}
                          <div className="rounded-lg border border-white/5 p-3" style={{ background: 'rgba(255,255,255,0.015)' }}>
                            <ActivitySparkline cases={o.cases} />
                          </div>

                          {/* Species profile */}
                          <div className="rounded-lg border border-white/5 p-3" style={{ background: 'rgba(255,255,255,0.015)' }}>
                            <p className="text-white/30 text-[9px] uppercase tracking-wider mb-2">Species Profile</p>
                            {o.species.length > 0 ? (
                              <div className="flex flex-wrap gap-1.5">
                                {o.species.map((sp: string) => (
                                  <span key={sp} className={`px-2 py-0.5 rounded-full border text-[9px] font-medium capitalize ${speciesChipCls(sp)}`}>
                                    {sp}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-white/20 text-[10px] italic">No species data</span>
                            )}
                          </div>
                        </div>

                        {/* Syndicate membership note */}
                        {(() => {
                          const net = syndicates.find((s) => s.members.some((m) => m.toLowerCase() === o.name.toLowerCase()));
                          if (!net) return null;
                          return (
                            <div className="mb-3 rounded-lg border border-violet-500/20 px-3 py-2 flex items-center gap-2" style={{ background: 'rgba(139,92,246,0.06)' }}>
                              <UsersIcon className="w-3.5 h-3.5 text-violet-400 shrink-0" />
                              <p className="text-violet-300/70 text-[10px]">
                                Member of criminal network with {net.members.length - 1} co-accused —{' '}
                              {net.members.filter((m) => m.toLowerCase() !== o.name.toLowerCase()).slice(0, 3).map((m, mi) => (
                                <span key={mi}>{mi > 0 && ', '}<MaskedName name={m} role={role} /></span>
                              ))}
                              {net.members.length > 4 ? ` +${net.members.length - 4} more` : ''}
                              </p>
                            </div>
                          );
                        })()}

                        {/* Linked cases */}
                        <p className="text-white/20 text-[9px] uppercase tracking-widest pb-2">
                          {o.cases.length} linked case{o.cases.length !== 1 ? 's' : ''} — click 🔔 to track status updates
                        </p>
                        <div className="flex flex-col gap-1.5">
                          {o.cases.map((c: any, ci: number) => {
                            const status  = c.caseStatus?.toLowerCase();
                            const tracked = c.id && trackedIds.has(c.id);
                            return (
                              <div
                                key={ci}
                                className="flex items-center gap-3 rounded-lg px-3 py-2.5 border border-white/5"
                                style={{ background: 'rgba(255,255,255,0.02)' }}
                              >
                                <div className="flex-1 min-w-0">
                                  <p className="text-white/70 text-xs font-medium capitalize truncate">{c.species || 'Unknown species'}</p>
                                  <p className="text-white/30 text-[10px] truncate mt-0.5">
                                    {c.location || '—'} &bull; {c.dateOfSeizure || '—'}
                                  </p>
                                </div>
                                <span className={`hidden sm:inline-flex px-2 py-0.5 rounded-full border text-[9px] font-semibold whitespace-nowrap ${STATUS_CLS[status] || 'bg-white/8 text-white/35 border-white/10'}`}>
                                  {c.caseStatus || 'Unknown'}
                                </span>
                                {isLoggedIn ? (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); c.id && toggle(c.id); }}
                                    title={tracked ? 'Stop tracking this case' : 'Track — get email alerts on status changes'}
                                    className={`shrink-0 text-base leading-none transition-all hover:scale-110 active:scale-95 ${tracked ? 'text-green-400' : 'text-white/15 hover:text-white/50'}`}
                                  >
                                    {tracked ? '🔔' : '🔕'}
                                  </button>
                                ) : (
                                  <span title="Log in to track cases" className="text-white/10 text-base cursor-default shrink-0">🔕</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
